"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import { useLanguage, languages } from "@/context/LanguageContext";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { language, setLanguage, t } = useLanguage();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navLinks = [
    { id: "nav_shop", labelKey: "nav_gallery" as const, href: "/online-shop" },
    { id: "nav_adventures", labelKey: "nav_adventures" as const, href: "/adventure-reservations" },
    { id: "nav_booking", labelKey: "nav_booking" as const, href: "/cart" },
  ];

  const isActive = (href: string) => pathname === href;

  const currentLang = languages.find((l) => l.code === language);

  // Close lang dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Scroll reveal setup
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -60px 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.reveal.will-animate');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, [pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/online-shop" className="flex items-center gap-2 group">
              <div className="flex items-end gap-[2px]">
                <div className="w-[2px] h-3 bg-primary"></div>
                <div className="w-[2px] h-5 bg-primary"></div>
                <div className="w-[2px] h-4 bg-primary"></div>
                <div className="w-[2px] h-2 bg-secondary"></div>
                <div className="w-[2px] h-5 bg-secondary ml-[1px]"></div>
                <div className="w-[2px] h-3 bg-accent"></div>
              </div>
              <span className="text-xl font-bold tracking-tight text-foreground">
                DJERBA<span className="text-muted-foreground">TREASURES</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  href={link.href}
                  className={`text-base font-medium transition-colors ${
                    isActive(link.href)
                      ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              ))}

              {/* Language Switcher Dropdown */}
              <div ref={langRef} className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors"
                  aria-label="Select language"
                >
                  <span>{currentLang?.label}</span>
                  <Icon name="ChevronDownIcon" size={14} className={`transition-transform ${isLangOpen ? "rotate-180" : ""}`} />
                </button>
                {isLangOpen && (
                  <div className="absolute right-0 top-full mt-2 w-40 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-muted ${
                          language === lang.code ? "text-primary font-semibold bg-primary/5" : "text-foreground"
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/21654604948"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-success text-white rounded-full font-semibold hover:bg-success/90 transition-all hover:scale-105"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                <span className="text-sm">{t("nav_whatsapp")}</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden flex flex-col gap-1.5 p-2"
              aria-label="Toggle menu"
            >
              <span className={`w-6 h-0.5 bg-foreground transition-all ${isMenuOpen ? "rotate-45 translate-y-2" : ""}`}></span>
              <span className={`w-6 h-0.5 bg-foreground transition-all ${isMenuOpen ? "opacity-0" : ""}`}></span>
              <span className={`w-6 h-0.5 bg-foreground transition-all ${isMenuOpen ? "-rotate-45 -translate-y-2" : ""}`}></span>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-background/98 backdrop-blur-md transition-transform duration-300 md:hidden ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              onClick={toggleMenu}
              className={`text-3xl font-bold transition-colors ${
                isActive(link.href)
                  ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {/* Mobile Language Switcher */}
          <div className="flex flex-wrap justify-center gap-2 max-w-xs">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsMenuOpen(false);
                }}
                className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                  language === lang.code
                    ? "bg-primary text-white border-primary" :"border-border hover:bg-muted"
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <a
            href="https://wa.me/21654604948"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-8 py-3 bg-success text-white rounded-full text-lg font-semibold hover:bg-success/90 transition-all"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
            <span>{t("nav_whatsapp")}</span>
          </a>
        </div>
      </div>
    </>
  );
}